import React, { forwardRef, useImperativeHandle, useCallback, useMemo, useState, useRef, useEffect } from "react"
import { Formik, Form } from "formik"
import { Button, Row, Col, message } from "antd"
import * as Yup from "yup"
import * as JobBoardFormFields from "./JobBoardFormFields"
import cssStyles from './CBSearch.module.css'

export const validationSchema = Yup.object().shape({
  keyword: Yup.string(),
  location: Yup.mixed()
    .test('is-valid-location', 'Invalid location format', function(value) {
      // Allow null/undefined
      if (value == null) return true;
      
      // Allow string values
      if (typeof value === 'string') return true;
      
      // Allow numerical values (location IDs)
      if (typeof value === 'number') return true;
      
      // Allow objects with locationId
      if (typeof value === 'object' && value !== null) {
        if (value.locationId) return true;
        if (value.id) return true; 
        if (value.name) return true;
      }
      
      // Reject everything else
      return false;
    }),
  radius: Yup.string(),
  postalCode: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, 'Please enter a valid ZIP code')
    .nullable(),
  jobTitle: Yup.string(),
  targetJobTitle: Yup.string(),
  recentCompany: Yup.string(),
  schoolName: Yup.string(),
  recentJobDescription: Yup.string(),
  keywordBehavior: Yup.string(),
  targetWorkLocation: Yup.mixed()
    .test('is-valid-target-location', 'Invalid target location format', function(value) {
      // Allow null/undefined
      if (value == null) return true;
      
      // Allow string values
      if (typeof value === 'string') return true;
      
      // Allow numerical values (location IDs)
      if (typeof value === 'number') return true;
      
      // Allow objects with locationId or name
      if (typeof value === 'object' && value !== null) {
        if (value.locationId) return true;
        if (value.id) return true; 
        if (value.name) return true;
      }
      
      // Reject everything else
      return false;
        }),
      targetWorkLocationBoolean: Yup.string(),
      targetWorkSite: Yup.string(),
      CBMinimumExperience: Yup.string(),
      CBMaximumExperience: Yup.string(),
      experience: Yup.array().of(Yup.string()),
      militaryStatus: Yup.array().of(Yup.string()),
      industry: Yup.array().of(Yup.string()),
      languageSkills: Yup.array().of(
        Yup.object().shape({
      language: Yup.string(),
      proficiency: Yup.string()
        })
      ),
      resumeLanguage: Yup.string(),
      educationMajor: Yup.string(),
      education: Yup.array().of(Yup.string()),
      employmentType: Yup.array().of(Yup.string()),
      careerLevel: Yup.array().of(Yup.string()),
      workAuthorization: Yup.array().of(Yup.string()),
      freshnessInDays: Yup.string(),
      compensationType: Yup.string(),
      minimumSalary: Yup.number().nullable(),
      maximumSalary: Yup.number().nullable(),
      securityClearance: Yup.array().of(Yup.string().oneOf([
        "12", "13", "14", "15", "16", "17"
      ])),
      militaryExperience: Yup.boolean(),
      managerialExperience: Yup.boolean(),
      projectLeadership: Yup.boolean(),
      currentlyEmployed: Yup.boolean(),
      willingToRelocate: Yup.boolean(),
      workStatus: Yup.string(),
      excludeIVRResumes: Yup.boolean(),
      excludeResumesWithNoSalary: Yup.boolean(),
      lemmatize: Yup.boolean(),
      removeDuplicate: Yup.boolean(),
      nicheInclusion: Yup.boolean(),
      applyLastActivity: Yup.boolean(),
      searchWithMostRecentJobTitle: Yup.boolean(),
      searchWithMostRecentCompany: Yup.boolean(),
      minimumTravelRequirement: Yup.string(),
      minimumDegree: Yup.string(),
      minimumEmployeesManaged: Yup.number().nullable(),
      maximumCommute: Yup.number().nullable(),
      languagesSpoken: Yup.array().of(Yup.string()),
      sortBy: Yup.string(),
      firstAlternateLocation: Yup.object().shape({
        city: Yup.string(),
        state: Yup.string(),
        zipCode: Yup.string(),
        country: Yup.string()
      }),
      secondAlternateLocation: Yup.object().shape({
        city: Yup.string(),
        state: Yup.string(),
        zipCode: Yup.string(),
        country: Yup.string()
      }),
  
      additionalParams: Yup.object().default({})
    }).test(
      'at-least-one-search-field',
      'Please enter at least a keyword or job title to search',
      function(values) {
    return !!(values.keyword || values.jobTitle || values.targetJobTitle);
  }
);

function arePropsEqual(prevProps, nextProps) {
  const compareKeys = ['fields', 'initialValues', 'layout', 'columns', 'formLayout', 'fieldGroups'];
  return compareKeys.every(key => {
    const prev = prevProps[key];
    const next = nextProps[key];
    return JSON.stringify(prev) === JSON.stringify(next);
  });
}

const MemoizedField = React.memo(({ field, getFieldLayout, parentOnFieldChange }) => {
  const FieldComponent = JobBoardFormFields[field.type];
  const fieldLayout = getFieldLayout(field);
  
  const additionalProps = parentOnFieldChange ? {
    customOnChange: (value) => {
      parentOnFieldChange(field.name, value);
    }
  } : {};
  
  return (
    <Col {...fieldLayout} key={field.name}>
      <FieldComponent 
        name={field.name} 
        label={field.label} 
        {...field.props} 
        {...additionalProps}
      />
    </Col>
  );
}, (prev, next) => {
  return JSON.stringify(prev.field) === JSON.stringify(next.field) &&
         JSON.stringify(prev.getFieldLayout(prev.field)) === JSON.stringify(next.getFieldLayout(next.field));
});

const JobBoardSearchForm = React.memo(forwardRef(({
  fields,
  initialValues,
  onSubmit,
  layout = "vertical",
  columns = 1,
  submitButtonText = "Search",
  resetButtonText = "Reset",
  showResetButton = true,
  formLayout = "default",
  fieldGroups,
  styles,
  onFieldChange,
}, ref) => {
 

  const [isSubmitting, setIsSubmitting] = useState(false);
  const mounted = useRef(true);
  // Debug logging helper
  const logFormEvent = (event, data) => {
    // console.log(`[JobBoardSearchForm] ${event}:`, data);
  };

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const getFieldLayout = useCallback((field) => {
    if (typeof formLayout === "object") {
      return formLayout[field.name] || {}
    }

    switch (formLayout) {
      case "sidebar":
        return { span: 24 }
      case "grouped":
        return { style: { margin: 0 } }
      case "default":
      default:
        return { span: 24 / columns }
    }
  }, [formLayout, columns]);

  const renderFields = useCallback(() => {
    if (formLayout === "grouped" && fieldGroups) {
      return fieldGroups.map((group, index) => (
        <div key={index} style={styles?.formGroup}>
          <div style={styles?.groupTitle}>{group.title}</div>
          <div style={styles?.fieldsGrid}>
            {group.fields.map(fieldName => {
              const field = fields?.find(f => f.name === fieldName);
              return field ? (
                <MemoizedField 
                  key={field.name}
                  field={field}
                  getFieldLayout={getFieldLayout}
                  parentOnFieldChange={onFieldChange}
                />
              ) : null;
            })}
          </div>
        </div>
      ));
    }

    return (
      <Row gutter={16}>
        {fields?.map(field => (
          <MemoizedField 
            key={field.name}
            field={field}
            getFieldLayout={getFieldLayout}
            parentOnFieldChange={onFieldChange}
          />
        ))}
      </Row>
    );
  }, [fields, fieldGroups, formLayout, styles, getFieldLayout, onFieldChange]);

 

  const handleSubmit = useCallback(async (values, formikHelpers) => {
    if (!mounted.current) return;
    
    logFormEvent('Form submitted with values', values);
    
    const processedValues = { ...values };
    
    if (processedValues.location) {
      logFormEvent('Processing location value', processedValues.location);
      
    }
    
    if (processedValues.targetWorkLocation) {
      logFormEvent('Processing target location value', processedValues.targetWorkLocation);

    }
    
    if (!processedValues.keyword && !processedValues.jobTitle && !processedValues.targetJobTitle) {
      message.error('Please enter at least a keyword or job title to search');
      formikHelpers.setSubmitting(false);
      return;
    }
    
    if (!processedValues.radius) {
      processedValues.radius = "30";
    }
    
    setIsSubmitting(true);
    
    try {
      logFormEvent('Calling onSubmit with processed values', processedValues);
      await onSubmit(processedValues);
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error('Error performing search. Please try again.');
    } finally {
      if (mounted.current) {
        setIsSubmitting(false);
        formikHelpers.setSubmitting(false);
      }
    }
  }, [onSubmit]);

  const formInstance = useMemo(() => ({
    initialValues: initialValues || {},
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true
  }), [initialValues, handleSubmit]);

  return (
    <div className={cssStyles.cbContainer}>
      <Formik {...formInstance}>
        {({ resetForm, submitForm, isSubmitting }) => {
          useEffect(() => {
            logFormEvent('Form functions available', { 
              submitForm: !!submitForm, 
              resetForm: !!resetForm,
              isSubmitting
            });
          }, []);
          
          useImperativeHandle(ref, () => ({
            form: {
              resetForm,
              submitForm,
              validateForm: async () => {
                try {
                  await submitForm();
                  return {};
                } catch (error) {
                  return error;
                }
              }
            }
          }));
          
          const handleSearchButtonClick = () => {
            logFormEvent('Search button clicked', { isSubmitting });
            submitForm();
          };
          
          const handleResetButtonClick = () => {
            logFormEvent('Reset button clicked');
            resetForm();
          };

          return (
            <Form layout={layout}>
              {renderFields()}
              {formLayout !== "grouped" && (
                <Row gutter={16}>
                  <Col>
                    <Button 
                      type="primary" 
                      onClick={handleSearchButtonClick} 
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      className={cssStyles.primaryButton}
                    >
                      {isSubmitting ? "Searching..." : submitButtonText}
                    </Button>
                  </Col>
                  {showResetButton && (
                    <Col>
                      <Button onClick={handleResetButtonClick}>{resetButtonText}</Button>
                    </Col>
                  )}
                </Row>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}), arePropsEqual);

JobBoardSearchForm.displayName = 'JobBoardSearchForm';

export default JobBoardSearchForm;

